import type { VaultData } from '../domain/models';
import { clearSessionKey, decryptVault, encryptVault, type VaultEnvelope } from './crypto';

const DB_NAME = 'coverage-timeline-local-vault';
const STORE = 'vault';
const KEY = 'primary';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains('device-key')) request.result.createObjectStore('device-key');
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(new Error('LOCAL_STORE_OPEN_FAILED'));
  });
}

async function transaction<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>, storeName=STORE): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    let result!: T;
    let request: IDBRequest<T>;
    try {
      request = run(tx.objectStore(storeName));
    } catch {
      db.close();
      reject(new Error('LOCAL_STORE_WRITE_FAILED'));
      return;
    }
    request.onsuccess = () => { result = request.result; };
    request.onerror = () => reject(new Error('LOCAL_STORE_WRITE_FAILED'));
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(new Error('LOCAL_STORE_TRANSACTION_FAILED')); };
    tx.onabort = () => { db.close(); reject(new Error('LOCAL_STORE_TRANSACTION_FAILED')); };
  });
}

export async function hasVault(): Promise<boolean> {
  return Boolean(await transaction('readonly', (store) => store.get(KEY)));
}

export async function readEnvelope(): Promise<VaultEnvelope | undefined> {
  return transaction('readonly', (store) => store.get(KEY));
}

export async function unlockVault(passphrase: string): Promise<VaultData> {
  const envelope = await readEnvelope();
  if (!envelope) throw new Error('VAULT_NOT_FOUND');
  return decryptVault(envelope, passphrase, envelope.version===2 ? await readDeviceKey() : undefined);
}

export async function saveVault(
  data: VaultData,
  passphrase: string,
  readBack: () => Promise<VaultEnvelope | undefined> = readEnvelope,
): Promise<VaultEnvelope> {
  const current = await readEnvelope();
  const envelope = await encryptVault(data, passphrase, current?.kdf.salt, current?.version===2 && !passphrase ? await readDeviceKey() : undefined);
  await transaction('readwrite', (store) => store.put(envelope, KEY));
  const stored = await readBack();
  if (!stored || !sameEnvelope(stored, envelope)) throw new Error('LOCAL_STORE_READBACK_FAILED');
  return stored;
}

export async function importVault(envelope: VaultEnvelope, passphrase: string): Promise<VaultData> {
  const data = await decryptVault(envelope, passphrase);
  await transaction('readwrite', (store) => store.put(envelope, KEY));
  const stored = await readEnvelope();
  if (!stored || !sameEnvelope(stored, envelope)) throw new Error('LOCAL_STORE_READBACK_FAILED');
  return data;
}

export async function deleteVault(): Promise<void> {
  clearSessionKey();
  const db=await openDb();
  await new Promise<void>((resolve,reject)=>{const tx=db.transaction([STORE,'device-key'],'readwrite');tx.objectStore(STORE).delete(KEY);tx.objectStore('device-key').clear();tx.oncomplete=()=>{db.close();resolve();};tx.onerror=()=>{db.close();reject(new Error('LOCAL_STORE_TRANSACTION_FAILED'));};});
}

export function downloadEnvelope(envelope: VaultEnvelope, filename = 'bodocha-i-backup.json'): void {
  if(envelope.version===2) throw new Error('PASSWORD_REQUIRED_FOR_BACKUP');
  const blob = new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(href);
}

function sameEnvelope(left: VaultEnvelope, right: VaultEnvelope): boolean {
  return left.format === right.format
    && left.version === right.version
    && left.kdf.name === right.kdf.name
    && left.kdf.hash === right.kdf.hash
    && left.kdf.iterations === right.kdf.iterations
    && left.kdf.salt === right.kdf.salt
    && left.cipher.name === right.cipher.name
    && left.cipher.iv === right.cipher.iv
    && left.cipher.data === right.cipher.data;
}

export async function readDeviceKey(): Promise<CryptoKey | undefined> {return transaction('readonly',store=>store.get(KEY),'device-key');}
export async function createDeviceVault(data:VaultData):Promise<VaultEnvelope> {
 if(await readEnvelope())throw new Error('VAULT_ALREADY_EXISTS');
 const key=await crypto.subtle.generateKey({name:'AES-GCM',length:256},false,['encrypt','decrypt']);
 await transaction('readwrite',store=>store.put(key,KEY),'device-key');
 try {
 const read=await readDeviceKey();if(!read || read.extractable) throw new Error('DEVICE_KEY_UNAVAILABLE');
 const envelope=await encryptVault(data,'',undefined,read);
 await decryptVault(envelope,'',read);
 await transaction('readwrite',store=>store.put(envelope,KEY));
 const stored=await readEnvelope();if(!stored||!sameEnvelope(stored,envelope))throw new Error('LOCAL_STORE_READBACK_FAILED');return stored;
 }catch(error){await transaction('readwrite',store=>store.delete(KEY),'device-key');throw error;}
}
export async function changeProtection(data:VaultData,{from,to}:{from:string;to:string},readBack=readEnvelope):Promise<VaultEnvelope>{
 const previous=await readEnvelope();if(!previous)throw new Error('VAULT_NOT_FOUND');
 await decryptVault(previous,from,previous.version===2?await readDeviceKey():undefined);
 const envelope=await encryptVault(data,to);
 try {
 await transaction('readwrite',store=>store.put(envelope,KEY));
 const stored=await readBack();if(!stored||!sameEnvelope(stored,envelope))throw new Error('LOCAL_STORE_READBACK_FAILED');
 await decryptVault(stored,to);
 await transaction('readwrite',store=>store.delete(KEY),'device-key');clearSessionKey();return stored;
 }catch(error){await transaction('readwrite',store=>store.put(previous,KEY));clearSessionKey();throw error;}
}
